import { useField } from 'formik';
import { ReactNode } from 'react';
import { inputClass } from 'shared/theme/colorTokens';
import { tw } from 'utils/tailwindUtils';
import FormField from './FormField';

export interface SelectOption<T> {
  value: T;
  label: string;
}

interface FormikSelectProps<T> {
  name: string;
  label?: ReactNode;
  options: SelectOption<T>[];
  disabled?: boolean;
  className?: string;
}

// `undefined`/`null` se representan como '' para poder tener una opción "Todos".
const toKey = (value: unknown) =>
  value === undefined || value === null ? '' : String(value);

/**
 * Select de Formik con el estilo estándar de los inputs (`<select>` nativo).
 * El valor guardado en Formik es el `value` original de la opción (número,
 * string o undefined), no el string del DOM.
 */
function FormikSelect<T>({
  name,
  label,
  options,
  disabled,
  className
}: FormikSelectProps<T>) {
  const [field, meta, helpers] = useField(name);

  return (
    <FormField
      label={label}
      htmlFor={name}
      error={meta.touched && meta.error}
      className={className}
    >
      <select
        id={name}
        name={name}
        disabled={disabled}
        value={toKey(field.value)}
        onBlur={field.onBlur}
        onChange={(e) => {
          const selected = options.find(
            (option) => toKey(option.value) === e.target.value
          );
          helpers.setValue(selected ? selected.value : undefined);
        }}
        className={tw(
          inputClass,
          '[&>option]:bg-whiteLight dark:[&>option]:bg-darkLight'
        )}
      >
        {options.map((option) => (
          <option key={toKey(option.value)} value={toKey(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    </FormField>
  );
}

export default FormikSelect;
